import React from 'react';
import { Link } from 'react-router-dom';
import { Scale, Phone, MapPin, Clock, ExternalLink, Users, Shield, Heart, Globe, FileText } from 'lucide-react';
import Newsletter from '../components/Newsletter';
import { useLanguage } from '../context/LanguageContext';

const legalResources = [
  {
    category: 'Immigration Legal Services',
    icon: Globe,
    color: 'teal',
    organizations: [
      {
        name: 'Immigrant Law Center of Minnesota (ILCM)',
        description: 'Free legal representation for low-income immigrants and refugees on adjustment of status, DACA, naturalization, TPS, T visas, and family petitions.',
        phone: '651-641-1011',
        altPhone: '1-800-223-1368',
        address: '450 N. Syndicate St., Suite 200, St. Paul, MN',
        website: 'https://www.ilcm.org',
        hours: 'Mon-Thu intake hours vary',
        services: ['Adjustment of Status', 'DACA', 'Naturalization', 'TPS', 'T/U Visas', 'Family Petitions']
      },
      {
        name: 'The Advocates for Human Rights',
        description: 'Free immigration and human rights legal help through Refugee & Asylum Project and statewide clinics.',
        phone: '612-341-3302',
        address: '330 Second Ave S, Suite 800, Minneapolis, MN',
        website: 'https://www.theadvocatesforhumanrights.org',
        hours: 'Business hours',
        services: ['Asylum', 'Refugee Services', 'Human Rights Cases', 'Immigration']
      },
      {
        name: 'Mid-Minnesota Legal Aid - Immigration',
        description: 'Free advice and representation for immigrants on family violence, adjustment of status, and citizenship.',
        phone: '612-332-1441',
        address: 'Multiple Twin Cities locations',
        website: 'https://www.mylegalaid.org',
        hours: 'Business hours',
        services: ['Family Violence Cases', 'Citizenship', 'Know Your Rights']
      }
    ]
  },
  {
    category: 'Pro Bono & Legal Aid Clinics',
    icon: Users,
    color: 'purple',
    organizations: [
      {
        name: 'Volunteer Lawyers Network (VLN)',
        description: 'Pro bono legal clinics for immigration and family law throughout the Twin Cities.',
        phone: '612-752-6655',
        address: '600 Nicollet Mall, Suite 390A, Minneapolis, MN',
        website: 'https://www.vlnmn.org',
        hours: 'Walk-in clinic hours vary',
        services: ['Immigration Clinics', 'Family Law', 'Housing', 'Expungement'],
        clinics: [
          { name: 'Ascension North Minneapolis', day: 'Wednesdays', time: 'Call for times' },
          { name: 'Dignity Center South Minneapolis', day: '1st/3rd Wednesdays', time: '2:30-4:30pm' },
          { name: 'CLUES St. Paul', day: '3rd Tuesdays', time: '12:00-2:00pm' }
        ]
      },
      {
        name: 'Southern Minnesota Regional Legal Services (SMRLS)',
        description: 'Free and low-cost legal services for immigration, family, and employment matters.',
        phone: '651-228-9823',
        altPhone: '1-888-575-2954',
        address: '55 East 5th St., Suite 400, St. Paul, MN',
        website: 'https://www.smrls.org',
        hours: 'Mon-Fri 8:30am-4:30pm',
        services: ['Immigration', 'Family Law', 'Employment', 'Housing', 'Public Benefits']
      },
      {
        name: 'Waite House Immigration Clinic (MMLA)',
        description: 'Walk-in immigration legal clinic serving the Phillips neighborhood.',
        phone: '612-332-1441',
        address: '2323 11th Ave S, Minneapolis, MN',
        website: 'https://www.mylegalaid.org',
        hours: 'Tues/Thurs 10am-3:30pm',
        services: ['Immigration Consultations', 'Document Review', 'Referrals']
      }
    ]
  },
  {
    category: 'Family Law & Civil Rights',
    icon: Heart,
    color: 'red',
    organizations: [
      {
        name: 'Tubman Family Alliance',
        description: 'Legal advocacy for survivors of domestic violence, including immigration relief options.',
        phone: '612-825-0000',
        address: 'Multiple Twin Cities locations',
        website: 'https://www.tubman.org',
        hours: '24/7 Crisis Line',
        services: ['Domestic Violence', 'Legal Advocacy', 'VAWA Petitions', 'Safety Planning']
      },
      {
        name: 'Legal Aid Society of Minneapolis',
        description: 'Civil legal services for low-income individuals in family, housing, and consumer matters.',
        phone: '612-334-5970',
        address: 'Minneapolis, MN',
        website: 'https://www.lasmpls.org',
        hours: 'Business hours',
        services: ['Family Law', 'Housing', 'Consumer Rights', 'Public Benefits']
      },
      {
        name: 'Hennepin County Bar Association Lawyer Referral',
        description: 'Referrals to attorneys offering reduced-fee consultations.',
        phone: '612-752-6666',
        address: 'Minneapolis, MN',
        website: 'https://www.hcba.org',
        hours: 'Business hours',
        services: ['Attorney Referrals', 'Reduced-Fee Consultations']
      }
    ]
  },
  {
    category: 'Human Rights Organizations',
    icon: Shield,
    color: 'amber',
    organizations: [
      {
        name: 'Minnesota Department of Human Rights',
        description: 'Investigates discrimination complaints in employment, housing, education, and public services.',
        phone: '651-539-1100',
        altPhone: '1-800-657-3704',
        address: 'Freeman Building, 625 Robert St N, St. Paul, MN',
        website: 'https://mn.gov/mdhr',
        hours: 'Mon-Fri 8am-4:30pm',
        services: ['Discrimination Complaints', 'Civil Rights Enforcement', 'Education']
      },
      {
        name: 'ACLU of Minnesota',
        description: 'Defends civil liberties through litigation, advocacy, and public education.',
        phone: '651-645-4097',
        address: '2300 Myrtle Ave, Suite 180, St. Paul, MN',
        website: 'https://www.aclu-mn.org',
        hours: 'Business hours',
        services: ['Civil Liberties', 'Know Your Rights', 'Impact Litigation']
      },
      {
        name: 'CAIR-MN (Council on American-Islamic Relations)',
        description: 'Civil rights advocacy and legal services for those facing discrimination.',
        phone: '612-206-3360',
        address: 'Minneapolis, MN',
        website: 'https://www.cairmn.com',
        hours: 'Business hours',
        services: ['Civil Rights', 'Discrimination Cases', 'Know Your Rights']
      }
    ]
  },
  {
    category: 'Additional Resources',
    icon: FileText,
    color: 'stone',
    organizations: [
      {
        name: 'International Institute of Minnesota',
        description: 'Free naturalization assistance and citizenship eligibility guidance.',
        phone: '651-647-0191',
        address: '1694 Como Ave, St. Paul, MN',
        website: 'https://www.iimn.org',
        hours: 'Business hours',
        services: ['Citizenship Classes', 'Naturalization Help', 'Refugee Services']
      },
      {
        name: 'MN Immigration Legal Services Hub',
        description: 'Statewide network connecting immigrants to free legal consultations.',
        phone: 'Various clinic locations',
        website: 'https://www.lawhelpmn.org',
        hours: 'Clinic hours vary',
        services: ['Free Consultations', 'Clinic Referrals', 'Know Your Rights']
      },
      {
        name: 'Justia Legal Aid Directory',
        description: 'Online directory of pro bono and legal aid services in Minnesota.',
        website: 'https://www.justia.com/lawyers/family-law/minnesota/legal-aid-and-pro-bono-services',
        hours: 'Online resource',
        services: ['Attorney Search', 'Legal Aid Directory']
      }
    ]
  }
];

const colorClasses = {
  teal: { bg: 'bg-teal-100', text: 'text-teal-700', icon: 'text-teal-600', border: 'border-teal-200' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-700', icon: 'text-purple-600', border: 'border-purple-200' },
  red: { bg: 'bg-red-100', text: 'text-red-700', icon: 'text-red-600', border: 'border-red-200' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-700', icon: 'text-amber-600', border: 'border-amber-200' },
  stone: { bg: 'bg-stone-100', text: 'text-stone-700', icon: 'text-stone-600', border: 'border-stone-200' }
};

const LegalHelp = () => {
  const { t } = useLanguage();
  
  return (
    <div className="bg-stone-50 pt-20">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-b from-purple-700 to-purple-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-6">
            <Scale size={16} />
            <span>{t('legalHelp.badge')}</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t('legalHelp.heroTitle')}
          </h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">
            {t('legalHelp.heroSubtitle')}
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:651-641-1011"
              className="px-6 py-3 bg-amber-500 hover:bg-amber-400 text-stone-900 font-semibold rounded-lg transition-colors flex items-center gap-2"
            >
              <Phone size={18} />
              ILCM Hotline: 651-641-1011
            </a>
            <a
              href="tel:612-752-6655"
              className="px-6 py-3 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
            >
              <Phone size={18} />
              VLN: 612-752-6655
            </a>
          </div>
        </div>
      </section>

      {/* Emergency Legal Help Banner */}
      <section className="py-4 bg-red-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Shield className="text-white" size={24} />
              <div>
                <span className="font-bold text-white">{t('legalHelp.emergencyBanner.title')}</span>
                <span className="text-white/90 ml-2">{t('legalHelp.emergencyBanner.subtitle')}</span>
              </div>
            </div>
            <a href="tel:651-641-1011" className="px-6 py-2 bg-white text-red-600 font-bold rounded-lg hover:bg-red-50 transition-colors">
              651-641-1011
            </a>
          </div>
        </div>
      </section>

      {/* Know Your Rights */}
      <section className="py-12 bg-amber-50 border-b border-amber-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 bg-white rounded-xl border border-amber-200">
              <h3 className="font-bold text-stone-800 mb-2">{t('legalHelp.knowYourRights.silent.title')}</h3>
              <p className="text-sm text-stone-600">{t('legalHelp.knowYourRights.silent.desc')}</p>
            </div>
            <div className="p-6 bg-white rounded-xl border border-amber-200">
              <h3 className="font-bold text-stone-800 mb-2">{t('legalHelp.knowYourRights.attorney.title')}</h3>
              <p className="text-sm text-stone-600">{t('legalHelp.knowYourRights.attorney.desc')}</p>
            </div>
            <div className="p-6 bg-white rounded-xl border border-amber-200">
              <h3 className="font-bold text-stone-800 mb-2">{t('legalHelp.knowYourRights.refuseEntry.title')}</h3>
              <p className="text-sm text-stone-600">{t('legalHelp.knowYourRights.refuseEntry.desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Legal Resources */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {legalResources.map((category, catIndex) => {
            const Icon = category.icon;
            const colors = colorClasses[category.color];
            
            return (
              <div key={catIndex} className="mb-16 last:mb-0">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`p-3 rounded-xl ${colors.bg}`}>
                    <Icon className={colors.icon} size={24} />
                  </div>
                  <h2 className="text-2xl font-bold text-stone-800">{category.category}</h2>
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {category.organizations.map((org, orgIndex) => (
                    <div key={orgIndex} className={`bg-white rounded-xl border ${colors.border} shadow-sm overflow-hidden`}>
                      <div className="p-6">
                        <h3 className="text-lg font-bold text-stone-800 mb-2">{org.name}</h3>
                        <p className="text-sm text-stone-600 mb-4">{org.description}</p>
                        
                        {org.phone && (
                          <div className="flex items-center gap-2 mb-2">
                            <Phone size={14} className="text-stone-400" />
                            <a href={`tel:${org.phone.replace(/[^0-9]/g, '')}`} className="text-teal-600 hover:text-teal-700 font-medium">
                              {org.phone}
                            </a>
                            {org.altPhone && (
                              <span className="text-stone-400">/ <a href={`tel:${org.altPhone.replace(/[^0-9]/g, '')}`} className="text-teal-600 hover:text-teal-700">{org.altPhone}</a></span>
                            )}
                          </div>
                        )}
                        
                        {org.address && (
                          <div className="flex items-start gap-2 mb-2">
                            <MapPin size={14} className="text-stone-400 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-stone-600">{org.address}</span>
                          </div>
                        )}
                        
                        {org.hours && (
                          <div className="flex items-center gap-2 mb-3">
                            <Clock size={14} className="text-stone-400" />
                            <span className="text-sm text-stone-500">{org.hours}</span>
                          </div>
                        )}
                        
                        {org.services && (
                          <div className="flex flex-wrap gap-1 mb-4">
                            {org.services.slice(0, 4).map((service, i) => (
                              <span key={i} className={`px-2 py-0.5 text-xs rounded-full ${colors.bg} ${colors.text}`}>
                                {service}
                              </span>
                            ))}
                            {org.services.length > 4 && (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-stone-100 text-stone-500">
                                +{org.services.length - 4} more
                              </span>
                            )}
                          </div>
                        )}
                        
                        {org.clinics && (
                          <div className="mb-4 p-3 bg-stone-50 rounded-lg">
                            <p className="text-xs font-semibold text-stone-600 uppercase mb-2">Walk-in Clinics</p>
                            {org.clinics.map((clinic, i) => (
                              <p key={i} className="text-xs text-stone-600">
                                <span className="font-medium">{clinic.name}:</span> {clinic.day}, {clinic.time}
                              </p>
                            ))}
                          </div>
                        )}
                        
                        {org.website && (
                          <a
                            href={org.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 font-medium"
                          >
                            Visit Website
                            <ExternalLink size={14} />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Report Civil/Human Rights Violations */}
      <section className="py-16 bg-purple-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Scale className="text-white mx-auto mb-4" size={48} />
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            {t('legalHelp.reportViolation.title')}
          </h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            {t('legalHelp.reportViolation.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/report-violation"
              className="px-8 py-4 bg-amber-500 hover:bg-amber-400 text-stone-900 font-semibold rounded-lg transition-colors"
            >
              {t('legalHelp.reportViolation.reportButton')}
            </Link>
            <Link
              to="/alert-map"
              className="px-8 py-4 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-lg transition-colors"
            >
              {t('legalHelp.reportViolation.viewMap')}
            </Link>
          </div>
        </div>
      </section>

      <Newsletter />
    </div>
  );
};

export default LegalHelp;
